import { AlertTriangle, Check, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import type { CurriculumRemovalNode } from "../services/curriculum.service";
import { orderRemovals, removalSummary } from "./curriculum-removals";

type Props = {
  nodes: CurriculumRemovalNode[] | null;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
};

/**
 * Names what a structure save would drop before letting it through.
 *
 * The API refuses to unassign a published or already-scheduled node unless the
 * save names it, so this is the only route to a deliberate deletion. Rows are
 * ordered scheduled-first: losing a lesson a student holds is the consequence
 * worth reading, and unpublished-but-unassigned is comparatively cheap.
 */
export const ConfirmRemovalsDialog = ({
  nodes,
  submitting,
  onCancel,
  onConfirm,
}: Props) => {
  const ordered = orderRemovals(nodes);

  return (
    <Dialog
      open={nodes !== null}
      onOpenChange={(open) => !open && !submitting && onCancel()}
    >
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-destructive" strokeWidth={2} />
            <DialogTitle className="text-xl">Remove these lessons?</DialogTitle>
          </div>
          <DialogDescription>
            {removalSummary(nodes)}
          </DialogDescription>
        </DialogHeader>

        <ul className="max-h-64 divide-y overflow-y-auto rounded-lg border text-sm">
          {ordered.map((node) => (
            <li
              key={node.id}
              className="flex items-center justify-between gap-3 px-4 py-2.5"
            >
              <span className="min-w-0 truncate font-medium">
                {node.name ?? `Lesson ${node.id}`}
              </span>
              <span
                className={
                  node.scheduled
                    ? "shrink-0 text-xs font-semibold text-destructive"
                    : "shrink-0 text-xs text-muted-foreground"
                }
              >
                {node.scheduled ? "assigned to students" : "not assigned"}
              </span>
            </li>
          ))}
        </ul>

        <p className="text-sm text-muted-foreground">
          Cancelling leaves these lessons in place. No structure change has been
          saved.
        </p>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={submitting}
            className="rounded-lg px-6"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirm}
            disabled={submitting}
            className="gap-2 rounded-lg px-6"
          >
            {submitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Check className="h-4 w-4" />
            )}
            {submitting ? "Saving…" : "Remove and save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
